import { useEffect, useState } from 'react';
import type { PlatformOverview } from '@starter/shared';
import { api, ApiError } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import { LoadingState } from '../../components/LoadingState.js';
import { PageHeader } from '../../components/PageHeader.js';

export function PlatformOverviewPage() {
  const [data, setData] = useState<PlatformOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    api
      .platformOverview()
      .then(setData)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load overview'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  if (loading) return <LoadingState message="Loading platform overview…" />;
  if (error) return <ErrorBanner message={error} onRetry={load} />;
  if (!data) return null;

  const roleSummary = Object.entries(data.users.membershipsByRole)
    .map(([role, count]) => `${role.replace(/_/g, ' ')}: ${count}`)
    .join(' · ');

  return (
    <div className="page">
      <PageHeader title="Platform overview" subtitle="Operational snapshot across tenants" />
      <div className="stat-grid stat-grid--wide">
        <div className="card stat-card">
          <span className="stat-card__label">Organizations</span>
          <span className="stat-card__value">{data.organizations.total}</span>
          <p className="stat-card__meta">
            {data.organizations.active} active · {data.organizations.inactive} inactive
          </p>
        </div>
        <div className="card stat-card">
          <span className="stat-card__label">Programs</span>
          <span className="stat-card__value">{data.programs.total}</span>
          <p className="stat-card__meta">
            {data.programs.active} active · {data.programs.inactive} inactive
          </p>
        </div>
        <div className="card stat-card">
          <span className="stat-card__label">Active users</span>
          <span className="stat-card__value">{data.users.active}</span>
          {roleSummary && <p className="stat-card__meta">{roleSummary}</p>}
        </div>
        <div className="card stat-card">
          <span className="stat-card__label">Platform admins</span>
          <span className="stat-card__value">{data.platformAdmins.total}</span>
          <p className="stat-card__meta">{data.platformAdmins.active} active</p>
        </div>
        <div className="card stat-card">
          <span className="stat-card__label">Worker status</span>
          <span className="stat-card__value stat-card__value--text">{data.workers.status}</span>
          <p className="stat-card__meta">
            {data.workers.summary} · {data.workers.instanceCount} instance
            {data.workers.instanceCount === 1 ? '' : 's'}
          </p>
        </div>
        <div className="card stat-card">
          <span className="stat-card__label">Application</span>
          <span className="stat-card__value stat-card__value--text">{data.application.status}</span>
          <p className="stat-card__meta">Database: {data.application.database.status}</p>
        </div>
      </div>
    </div>
  );
}
